import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Truck, RefreshCw, Clock, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { usePricing } from '../hooks/usePricing';
import { useCheckoutUrl } from '../hooks/useCheckoutUrl';
export function ProductOffer() {
  const checkoutUrl = useCheckoutUrl();
  const {
    formattedPrice,
    formattedOriginalPrice,
    isLoading
  } = usePricing();
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [viewers, setViewers] = useState(24);
  const [spotsLeft, setSpotsLeft] = useState(15);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        const next = prev + change;
        return Math.min(Math.max(next, 18), 32);
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setSpotsLeft(prev => {
        if (prev <= 3) return 3;
        return Math.random() > 0.6 ? prev - 1 : prev;
      });
    }, 12000);
    return () => clearInterval(interval);
  }, []);
  const maxSpots = 20;
  const progressPercent = (maxSpots - spotsLeft) / maxSpots * 100;
  return <section id="producto" className="py-10 md:py-16 bg-chocolate text-white" data-id="element-282">
      <div className="max-w-xl mx-auto px-4" data-id="element-283">
        {/* Title + Subtitle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8" data-id="element-284">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta/20 border border-terracotta/30 rounded-full mb-5 animate-pulse" data-id="element-285">
            <Clock size={14} className="text-terracotta" data-id="element-286" />
            <span className="text-sm font-bold text-terracotta" data-id="element-287">
              Offer ends in {formatTime(timeLeft)}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3" data-id="element-288">
            Don't miss this <span className="text-copper" data-id="element-289">opportunity</span>
          </h2>
          <p className="text-white/60 max-w-lg mx-auto text-sm" data-id="element-290">
            Get access to the complete collection with all bonuses included.
            This offer will not be available forever.
          </p>
        </motion.div>

        {/* Product Image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="rounded-xl overflow-hidden shadow-2xl mb-0" data-id="element-291">
          <img src="https://cdn.magicpatterns.com/uploads/fHCgHgqG1pxZierp27c2e7/20260302_1546_Image_Generation_remix_01kjqxw5xgf1att8cx1dd0mjw9.png" alt="Mega Collection Amigurumi Pets + 5 Bonuses" className="w-full" data-id="element-292" />
        </motion.div>

        {/* Shopify-style product card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-b-2xl rounded-t-none p-5 md:p-7 text-deep-brown shadow-2xl" data-id="element-293">
          {/* Product Name */}
          <h3 className="text-xl md:text-2xl font-heading font-bold text-deep-brown mb-2 leading-tight" data-id="element-294">
            🐶🐾 Mega Collection Amigurumi Pets by Breed + 5 BONUSES 🎁
          </h3>

          {/* Stars */}
          <div className="flex items-center gap-2 mb-4" data-id="element-295">
            <div className="text-amber-400 flex text-sm" data-id="element-296">★★★★★</div>
            <span className="text-gray-500 text-sm font-medium" data-id="element-297">
              4.9/5 (520 verified reviews)
            </span>
          </div>

          {/* Price */}
          <div className="flex items-center gap-3 mb-1" data-id="element-298">
            <span className="text-3xl md:text-4xl font-bold text-terracotta" data-id="element-299">
              {isLoading ? '...' : formattedPrice}
            </span>
            <span className="text-gray-400 line-through text-lg" data-id="element-300">
              {isLoading ? '...' : formattedOriginalPrice}
            </span>
            <span className="bg-terracotta text-white text-xs font-bold px-2.5 py-1 rounded-md" data-id="element-301">
              70% OFF
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4 italic" data-id="element-302">
            Price shown in your local currency at checkout.
          </p>

          {/* Urgency box */}
          <div className="bg-linen rounded-lg p-3.5 mb-4 border border-copper/15" data-id="element-303">
            <div className="flex items-center gap-2 text-deep-brown text-sm font-medium mb-2" data-id="element-304">
              <span className="relative flex h-3 w-3" data-id="element-305">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal opacity-75" data-id="element-306"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-teal" data-id="element-307"></span>
              </span>
              {viewers} people viewing this page
            </div>
            <div className="flex items-center gap-2 text-deep-brown text-sm font-medium" data-id="element-308">
              <Clock size={15} className="text-copper" data-id="element-309" />
              Offer expires in{' '}
              <span className="bg-terracotta text-white font-bold px-2 py-0.5 rounded text-sm font-mono" data-id="element-310">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          {/* Scarcity progress bar */}
          <div className="mb-4" data-id="element-311">
            <div className="flex items-center justify-between text-sm mb-1.5" data-id="element-312">
              <span className="text-terracotta font-bold flex items-center gap-1.5" data-id="element-313">
                <AlertCircle size={14} data-id="element-314" />
                🔥 Only {spotsLeft} discounted spots left
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden" data-id="element-315">
              <motion.div className="h-full bg-gradient-to-r from-terracotta to-red-500 rounded-full" initial={{
              width: '0%'
            }} animate={{
              width: `${progressPercent}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} data-id="element-316" />
            </div>
            <p className="text-xs text-gray-400 mt-1" data-id="element-317">
              {maxSpots - spotsLeft} of {maxSpots} spots already taken
            </p>
          </div>

          {/* CTA Button */}
          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="btn-pulse w-full py-4 bg-copper text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:bg-copper/90 transition-all duration-300 block text-center mb-4 uppercase tracking-wide" data-id="element-318">
            Get my patterns →
          </a>

          {/* Side by side: Acceso inmediato + Garantía */}
          <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-319">
            <div className="flex items-center gap-2 bg-teal/5 border border-teal/15 rounded-lg p-3" data-id="element-320">
              <Zap size={16} className="text-teal flex-shrink-0" data-id="element-321" />
              <span className="text-xs font-bold text-deep-brown" data-id="element-322">
                Immediate access
              </span>
            </div>
            <div className="flex items-center gap-2 bg-teal/5 border border-teal/15 rounded-lg p-3" data-id="element-323">
              <RefreshCw size={16} className="text-teal flex-shrink-0" data-id="element-324" />
              <span className="text-xs font-bold text-deep-brown" data-id="element-325">
                7-day guarantee
              </span>
            </div>
          </div>

          {/* Payment Methods Image */}
          <div className="border-t border-gray-100 pt-4" data-id="element-326">
            <img src="https://cdn.magicpatterns.com/uploads/3FFZE6SX62bsFCMuZtkyxF/20260209_1407_Image_Generation_remix_01kh1nwysjfqcvry1q12n9m1jf_(1).png" alt="Guaranteed secure payment: Visa, Mastercard, Maestro, PayPal" className="w-full max-w-sm mx-auto" data-id="element-327" />
          </div>
        </motion.div>
      </div>
    </section>;
}